import { Box, Grid, Typography } from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { type FC, useEffect } from 'react';

import { DISPATCH_SYSTEM_NAMES } from '@core/constants/integrations';
import { type CompanyFormikType, defaultDispatchCustomer } from '@core/formik/company';
import type { DispatchCustomerSummary } from '@core/types/api/DispatchCustomerSummary/DispatchCustomerSummary';
import Enums from '@core/types/api/Enums';
import type { Integration } from '@core/types/api/Integration/Integration';
import type { List } from '@core/types/list';
import { hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { Input } from '@web/components/Input/Input';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { ApiLookupInput } from '@web/components/LookupInput/ApiLookupInput';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { SlabCard } from '@web/components/SlabCard/SlabCard';
import { SlabCardBody } from '@web/components/SlabCard/SlabCardBody';
import { SlabCardHeader } from '@web/components/SlabCard/SlabCardHeader';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { lookups } from '@web/utils/DomainHelpers';
import { getIntegrationLogo } from '@web/utils/integrationHelper';

type OnDispatchCustomerChangeOpts = {
  dispatchCustomerID: string | null;
  dispatchCustomerSummaryList: List<DispatchCustomerSummary> | undefined;
};

/**
 * Given the formikBag and the values from the dispatch customer onMatchChange
 * handler, calculate the correct displayOnly value and return it.
 */
export const OnDispatchCustomerChange = ({
  dispatchCustomerID,
  dispatchCustomerSummaryList,
}: OnDispatchCustomerChangeOpts): {
  creditStatusCode: string | null;
  creditStatusDescription: string | null;
} => {
  const dispatchCustomer = dispatchCustomerSummaryList?.items.find(
    (dc) => dc.id === dispatchCustomerID,
  );
  const displayOnly =
    dispatchCustomer === undefined
      ? { creditStatusCode: null, creditStatusDescription: null }
      : {
          creditStatusCode: dispatchCustomer.creditStatusCode,
          creditStatusDescription: dispatchCustomer.creditStatusDescription,
        };
  return displayOnly;
};

type DispatchCustomerFormProps = {
  integration: Integration;
};

const DispatchCustomerForm: FC<DispatchCustomerFormProps> = ({ integration }) => {
  const formikBag = useFormikContext<CompanyFormikType>();

  // initialize dispatch customer fields for the integration
  useEffect(() => {
    const path = `dispatchCustomers.${integration.id}`;
    const current = getIn(formikBag.values, path);
    if (!hasValue(current) || typeof current !== 'object') {
      formikBag.setFieldValue(path, defaultDispatchCustomer, false);
    } else if (!('displayOnly' in current)) {
      formikBag.setFieldValue(`${path}.displayOnly`, defaultDispatchCustomer.displayOnly, false);
    }
  }, [integration.id]);

  return (
    <SlabCard>
      <SlabCardHeader sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {getIntegrationLogo(integration.integrationType)}
        <Typography variant='h5'>{`${integration.name} (${DISPATCH_SYSTEM_NAMES[integration.integrationType]})`}</Typography>
      </SlabCardHeader>

      <SlabCardBody>
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <ApiLookupInput
            formState={formikBag.values}
            label='Dispatch customer'
            name={`dispatchCustomers.${integration.id}`}
            route={{
              barrel: 'GET dispatch customer summaries',
              args: (inputText, { constructQueryParams }) =>
                constructQueryParams(inputText, {
                  filterBy: [
                    {
                      name: 'integrationId',
                      operation: Enums.FilterOperation.Equals,
                      values: [integration.id],
                    },
                  ],
                }),
              transform: (dispatchCustomerList): LookupInputOption[] =>
                lookups({
                  ...dispatchCustomerList,
                  label: (dc) => dc.name,
                  sublabels: (dc) => [dc.code],
                }),
            }}
            onMatchChange={(dispatchCustomerID, dispatchCustomerSummaryList) => {
              const displayOnly = OnDispatchCustomerChange({
                dispatchCustomerID,
                dispatchCustomerSummaryList,
              });
              SetFormikValue(
                formikBag,
                `dispatchCustomers.${integration.id}.displayOnly`,
                displayOnly,
              );
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Input
                label='Credit status code'
                name={`dispatchCustomers.${integration.id}.displayOnly.creditStatusCode`}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                label='Credit status description'
                name={`dispatchCustomers.${integration.id}.displayOnly.creditStatusDescription`}
                disabled
              />
            </Grid>
          </Grid>
        </Box>
      </SlabCardBody>
    </SlabCard>
  );
};

export const DynamicDispatchCustomerLookup: FC = () => {
  const {
    isLoading: isLoadingIntegrations,
    isError: isErrorIntegrations,
    data: integrations,
  } = useSlabQuery('GET integrations', {});

  if (isLoadingIntegrations) {
    return <LoadingSpinner />;
  }

  if (isErrorIntegrations || integrations === undefined) {
    return <div>Failed to load integrations</div>;
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} paddingBottom='1rem'>
          {integrations.items.map((integration) => (
            <DispatchCustomerForm key={integration.id} integration={integration} />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};
